import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, KeyRound, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";
import ThemeToggle from "../components/ThemeToggle";
import BrandLogo from "../components/BrandLogo";

export default function ForgotPassword() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      // Backend intentionally always returns ok, so any error here is network-level.
      toast({ title: "Couldn't send reset link", description: "Please try again in a moment.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen login-bg flex flex-col">
      <div className="absolute top-4 right-4 z-10"><ThemeToggle /></div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <BrandLogo variant="full" className="h-48 w-auto" />
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-xl shadow-primary/5 p-6 sm:p-8" data-testid="forgot-password-card">
            {!sent ? (
              <>
                <div className="flex items-center gap-2 text-primary text-[12px] font-semibold uppercase tracking-wider">
                  <KeyRound className="w-4 h-4" /> Password reset
                </div>
                <h1 className="mt-2 text-[22px] font-semibold tracking-tight">Forgot your password?</h1>
                <p className="text-[13px] text-muted-foreground mt-1">
                  Enter your work email and we&apos;ll send a link to choose a new password.
                </p>
                <form onSubmit={onSubmit} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" data-testid="forgot-email-input" type="email" autoComplete="email"
                      value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <Button type="submit" data-testid="forgot-submit-button" disabled={busy || !email} className="w-full h-10 gap-2">
                    <Mail className="w-4 h-4" /> {busy ? "Sending…" : "Send reset link"}
                  </Button>
                </form>
              </>
            ) : (
              <div data-testid="forgot-sent-panel">
                <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 text-[12px] font-semibold uppercase tracking-wider">
                  <Mail className="w-4 h-4" /> Check your inbox
                </div>
                <h1 className="mt-2 text-[22px] font-semibold tracking-tight">Reset link sent</h1>
                <p className="text-[13px] text-muted-foreground mt-1">
                  If an account exists for <strong>{email}</strong>, a reset link has been queued. The link expires in 2 hours.
                </p>
                <p className="text-[12px] text-muted-foreground mt-3">
                  Email delivery is currently <em>stubbed</em>. Reset links are recorded in the Activity Log
                  until a provider (Resend) is configured.
                </p>
              </div>
            )}
            <div className="mt-6 border-t border-border pt-4">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-[12.5px] text-primary hover:underline" data-testid="back-to-login-link">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </div>
          </div>
          <div className="mt-6 text-center text-[11.5px] text-muted-foreground">
            © {new Date().getFullYear()} UC Vigil · Internal staff portal
          </div>
        </div>
      </div>
    </div>
  );
}
